import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useField, useForm } from 'react-final-form';
import { observer } from 'mobx-react';
import { Button } from 'ksnm-common-ui/lib/Button';
import { FormContext } from 'ksnm-common-ui/lib/Form/FormWrapper/FormWrapperContext';
import { SelectField } from 'ksnm-common-ui/lib/Form/SelectField';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { uniqBy } from 'lodash-es';

import { reportGroupsStore } from '@stores/dictionaryStores/reportGroupsStore/reportGroupsStore';
import { passingControlStore } from '@stores/passingControlStore';

import { ControlVersionsList } from './ControlVersionsList';
import { useGetReportPackages } from './ControlVersionsList/useGetReportPackages';

import styles from '../AddControlVersionsDrawer.module.scss';

export const Form = observer(({ organizationCode }: { organizationCode: string }) => {
  const [showInfo, setShowInfo] = useState(false);

  const { uncreatedControlVersions, getUncreatedControlVersions } = passingControlStore;
  const { reportGroups } = reportGroupsStore;

  const { submit } = useForm();
  const { resetForm } = useContext(FormContext);

  const reportPackages = useGetReportPackages(organizationCode);

  const {
    input: { value: year },
  } = useField('year');

  const {
    input: { value: group },
  } = useField('group');

  const {
    input: { value: reportPackageCode },
  } = useField('reportPackageCode');

  const {
    input: { value: controlVersions },
  } = useField('controlVersions');

  useEffect(() => {
    (async () => {
      if (reportPackageCode) {
        const versions = await getUncreatedControlVersions(reportPackageCode);

        if (versions) setShowInfo(versions?.length === 0);
      }
    })();
  }, [getUncreatedControlVersions, reportPackageCode]);

  const onChangeYear = useCallback(
    (v) => {
      resetForm({ year: v, controlVersions: [] });
    },
    [resetForm],
  );

  const onChangeGroup = useCallback(
    (v) => {
      resetForm({ year: year, group: v, controlVersions: [] });
    },
    [resetForm, year],
  );

  const onChangePackage = useCallback(
    (v) => {
      resetForm({ year: year, group: group, reportPackageCode: v, controlVersions: [] });
    },
    [group, resetForm, year],
  );

  const yearItems = useMemo(
    () =>
      uniqBy(reportPackages, 'year').map((item) => ({
        name: item.year,
        id: item.year,
      })),
    [reportPackages],
  );

  const groupItems = useMemo(
    () =>
      uniqBy(
        reportPackages.filter((item) => item.year === year),
        'report_group_code',
      ).map((item) => {
        const reportGroup = reportGroups.find((g) => g.code === item.report_group_code);
        return {
          id: reportGroup?.code,
          name: `${reportGroup?.code} - ${reportGroup?.name}`,
        };
      }),
    [reportGroups, reportPackages, year],
  );

  const reportPackageCodeItems = useMemo(
    () =>
      reportPackages
        .filter((item) => item.year === year && item.report_group_code === group)
        .map((item) => ({ name: item.code, id: item.code })),
    [group, reportPackages, year],
  );

  return (
    <>
      <SelectField name="year" items={yearItems} label="Отчетный год" size="small" onChange={onChangeYear} />
      <SelectField
        name="group"
        items={groupItems}
        label="Код группы отчетности"
        size="small"
        onChange={onChangeGroup}
        disabled={!year}
      />
      <SelectField
        name="reportPackageCode"
        items={reportPackageCodeItems}
        label="Код пакета отчетов"
        size="small"
        onChange={onChangePackage}
        disabled={!group}
      />

      {reportPackageCode && <ControlVersionsList uncreatedControlVersions={uncreatedControlVersions} />}

      {showInfo && reportPackageCode && (
        <p className={styles.info}>Для данного пакета нет подходящих контрольных процедур</p>
      )}

      <Button
        className={styles.btn}
        icon={<Icon name="save" />}
        onClick={submit}
        disabled={!controlVersions.length}
        size="large"
      >
        Сохранить
      </Button>
    </>
  );
});
